import notifee, { AndroidImportance, AuthorizationStatus, EventType } from '@notifee/react-native';
import { AppState, Platform } from 'react-native';
import {
  ensureCallsChannel,
  ensureMessageChannel,
  invalidateAndroidChannels,
} from '../notifications/androidChannels';
import { tr } from '../i18n/runtime';
import { navigationRef } from '../navigation/navigationRef';
import {
  shouldSuppressIncomingCallPush,
  tryClaimMessageNotify,
} from '../notifications/pushDedupe';
import { isChatMuted } from '../screens/chatNotificationPrefsStorage';
import { callerAvatarFromPushData, presentIncomingCallUI } from '../calls/incomingCallUiBridge';
import { groupTitleFromPushData } from '../calls/callLabels';

let webPermissionAsked = false;
let nativeHandlerSet = false;
let fgUnsub: (() => void) | null = null;

/** Call after locale changes so Android channel labels match the UI language. */
export async function syncNotificationChannelsLocale(): Promise<void> {
  invalidateAndroidChannels();
  await ensureAndroidChannels();
}

async function ensureAndroidChannels(): Promise<void> {
  if (Platform.OS !== 'android') return;
  try {
    await ensureMessageChannel();
    await ensureCallsChannel();
  } catch {
    /* channel creation can be retried on next notification */
  }
}

function canonChatForNav(id: string): string {
  return id.trim().replace(/-/g, '').toLowerCase();
}

function dataToStringRecord(
  data: Record<string, unknown> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!data) return out;
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === 'string' ? v : String(v);
  }
  return out;
}

async function ensureNativeHandler(): Promise<void> {
  if (Platform.OS === 'web' || nativeHandlerSet) return;
  try {
    nativeHandlerSet = true;
    await ensureAndroidChannels();

    fgUnsub?.();
    fgUnsub = notifee.onForegroundEvent(async ({ type, detail }) => {
      if (type !== EventType.DELIVERED || !detail.notification) return;
      const data = (detail.notification.data ?? {}) as Record<string, unknown>;
      const t = data?.type;
      const muted = typeof data?.chatId === 'string' ? await isChatMuted(data.chatId) : false;

      if (t === 'message' && typeof data?.messageId === 'string') {
        if (AppState.currentState === 'active' && !tryClaimMessageNotify(data.messageId)) {
          const id = detail.notification.id;
          if (id) await notifee.cancelNotification(id);
          return;
        }
      } else if (t === 'call' && typeof data?.chatId === 'string') {
        const chatId = data.chatId;
        if (shouldSuppressIncomingCallPush(chatId)) {
          const id = detail.notification.id;
          if (id) await notifee.cancelNotification(id);
          return;
        }
        try {
          const route = navigationRef.getCurrentRoute();
          if (route?.name === 'Call') {
            const p = route.params as { chatId?: string; incoming?: boolean } | undefined;
            const dChat = String(chatId);
            if (
              p?.incoming &&
              p.chatId &&
              canonChatForNav(p.chatId) === canonChatForNav(dChat)
            ) {
              const id = detail.notification.id;
              if (id) await notifee.cancelNotification(id);
              return;
            }
          }
        } catch {
          /* ignore */
        }

        const callerName =
          typeof data.callerName === 'string'
            ? data.callerName
            : typeof data.title === 'string'
              ? data.title
              : tr('callGeneric');
        const callMode = data.callMode === 'video' ? 'video' : 'audio';
        const callScope = data.callScope === 'group' ? 'group' : 'direct';
        // Название группы едет вместе с аватаром во все три пути ниже (E2E-8).
        const avatarExtra = { ...callerAvatarFromPushData(data), ...groupTitleFromPushData(data) };
        const appState = AppState.currentState;
        if (appState === 'active' || appState === 'inactive') {
          if (Platform.OS === 'web') {
            presentIncomingCallUI({ chatId, callerName, callMode, callScope, ...avatarExtra });
          } else if (Platform.OS === 'ios') {
            // iOS: PushKit/CallKit already ringed via APNs VoIP push before
            // this notifee event fired. We just stash the payload so the
            // CallKit "Answer" tap can hydrate it; calling
            // displayIncomingCall again would create a duplicate call.
            try {
              const { storePendingCall } = await import(
                '../notifications/incomingCallBridge'
              );
              const uuid = chatId || `call-${Date.now()}`;
              storePendingCall(uuid, { chatId, callerName, callMode, callScope, ...avatarExtra });
            } catch {
              /* swallow */
            }
          } else {
            /**
             * Android: тот же in-app оверлей, что и на web.
             *
             * <p>Раньше здесь поднимался системный Telecom (`initCallKeep` →
             * `awaitAndroidTelecomForIncomingDisplay` → `displayIncomingCall`), и это
             * был единственный путь foreground-входящего. На свежей установке
             * «аккаунт звонков» в системе выключен, поэтому ожидание честно
             * выбирало весь свой пятисекундный таймаут, а `displayIncomingCall`
             * молча игнорировался — звонок не показывался вообще, пока
             * пользователь не включит приложение в настройках телефона.
             *
             * <p>Все остальные пути входящего на Android уже давно рисуют оверлей
             * (WS-инвайт в GlobalRealtimeProvider, фоновый FCM через
             * полноэкранное уведомление), так что Telecom тут был не только
             * ломающим, но и единственным исключением.
             */
            try {
              const inviteRaw = data.inviteId;
              presentIncomingCallUI({
                chatId,
                callerName,
                callMode,
                callScope,
                inviteId: typeof inviteRaw === 'string' ? inviteRaw : undefined,
                ...avatarExtra,
              });
            } catch {
              /* swallow — banner stays visible, user can still tap it */
            }
          }
        }
        const id = detail.notification.id;
        if (id) await notifee.cancelNotification(id);
        return;
      }

      if (t === 'message' || t === 'call') {
        if (muted) {
          const id = detail.notification.id;
          if (id) await notifee.cancelNotification(id);
        }
      }
    });
  } catch {
    /* no native module */
  }
}

/**
 * Подготовить показ уведомлений: обработчик и каналы Android. **Разрешение здесь больше не
 * спрашивается** (аудит удобства 2026-10-04, п. 34 и 15).
 *
 * <p>Раньше эта функция сама звала системный запрос — на подключении сокета, на каждом
 * входящем, на старте. На Android после первого входа это давало подряд три-четыре системных
 * окна без единого слова объяснения, а браузеры запрос «без жеста пользователя» глушат и
 * запоминают как отказ. Теперь запрос живёт в {@link requestNotifyPermission} и звучит только
 * по нажатию «Включить уведомления» — на карточке-пояснении после входа или в настройках.
 */
export async function ensureNotifyPermission(): Promise<void> {
  await ensureNativeHandler();
  if (Platform.OS === 'web') return;
  await ensureAndroidChannels();
}

/** Состояние разрешения на уведомления без запроса. */
export type NotifyPermissionStatus = 'granted' | 'denied' | 'undetermined' | 'unsupported';

export async function getNotifyPermissionStatus(): Promise<NotifyPermissionStatus> {
  if (Platform.OS === 'web') {
    if (typeof globalThis.Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    return Notification.permission === 'denied' ? 'denied' : 'undetermined';
  }
  try {
    const s = (await notifee.getNotificationSettings()).authorizationStatus;
    if (s === AuthorizationStatus.AUTHORIZED || s === AuthorizationStatus.PROVISIONAL) return 'granted';
    return s === AuthorizationStatus.NOT_DETERMINED ? 'undetermined' : 'denied';
  } catch {
    return 'unsupported';
  }
}

/**
 * Спросить разрешение — только из обработчика нажатия: браузер иначе молча откажет, а на
 * телефоне системное окно без пояснения перед ним читается как навязчивость.
 *
 * <p>После согласия токен FCM переотправляется на сервер: при входе он мог не уйти (в вебе
 * без разрешения токена нет вовсе), а без него не будет и push в фоне.
 */
export async function requestNotifyPermission(): Promise<NotifyPermissionStatus> {
  if (Platform.OS === 'web') {
    if (typeof globalThis.Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'default' && !webPermissionAsked) {
      webPermissionAsked = true;
      await Notification.requestPermission();
    }
  } else {
    try {
      await ensureAndroidChannels();
      await notifee.requestPermission();
    } catch {
      /* ниже статус скажет, что вышло */
    }
  }
  const status = await getNotifyPermissionStatus();
  if (status === 'granted') {
    void import('../notifications/push')
      .then(async (m) => {
        if (Platform.OS === 'web') await m.syncFcmTokenToBackend();
        else await m.refreshFcmRegistration();
      })
      .catch(() => {});
  }
  return status;
}

const MAX_NOTIFY_BODY_LEN = 400;

function truncateNotifyBody(text: string): string {
  const t = text.trim();
  if (t.length <= MAX_NOTIFY_BODY_LEN) return t;
  return `${t.slice(0, MAX_NOTIFY_BODY_LEN - 1)}…`;
}

export async function notifyIncomingMessage(opts: {
  chatId: string;
  title: string;
  body?: string;
  messageId?: string;
}): Promise<void> {
  await ensureNotifyPermission();
  if (await isChatMuted(opts.chatId)) {
    return;
  }
  const bodyText =
    typeof opts.body === 'string' && opts.body.trim().length > 0
      ? truncateNotifyBody(opts.body)
      : tr('notifyBodyMessage');
  if (Platform.OS === 'web') {
    if (typeof globalThis.Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(opts.title, { body: bodyText, tag: opts.chatId });
      } catch {
        /* ignore */
      }
    }
    return;
  }
  try {
    /**
     * Беззвучный канал — и это вся правка «на одно сообщение два звука».
     *
     * <p>Сюда попадают только уведомления живого приложения: `notifyIncomingMessage` зовут
     * из WS-обработчика, который прямо перед этим уже сыграл звук темы через
     * `playIncomingMessageSound()`. Канал со звуком добавлял к нему второй, системный.
     * Фоновый путь (`fcmBackground.android`) звук у канала оставляет — там играть некому.
     */
    const channelId = await ensureMessageChannel({ silent: true });
    await notifee.displayNotification({
      title: opts.title,
      body: bodyText,
      data: dataToStringRecord({
        chatId: opts.chatId,
        title: opts.title,
        type: 'message',
        ...(opts.messageId ? { messageId: opts.messageId } : {}),
      }),
      android: {
        channelId,
        importance: AndroidImportance.HIGH,
        pressAction: { id: 'default', launchActivity: 'default' },
      },
    });
  } catch {
    /* ignore */
  }
}

/** Системное уведомление о входящей заявке в друзья (Android data-only + foreground native). */
/**
 * Уведомление органайзера или дня рождения, пришедшее, пока приложение открыто.
 *
 * <p>В foreground система баннер не рисует ни на одной платформе — без этого вызова
 * сработавшее напоминание было бы видно только тому, кто в этот момент смотрел на список
 * напоминаний. Тем же способом, что и заявка в друзья рядом.
 *
 * @param type `reminder` | `birthday` | `scheduled_failed` | `comment_reply` — по нему клиент
 *     решает, что открыть по тапу; тег уведомления берётся отсюда же, поэтому напоминание не
 *     затирает поздравление, а ответ на комментарий — ни то, ни другое.
 */
export async function notifyOrganizerPush(opts: {
  type: string;
  title: string;
  body: string;
  extra?: Record<string, string>;
}): Promise<void> {
  await ensureNotifyPermission();
  const title = opts.title.trim();
  const bodyText = truncateNotifyBody(opts.body.trim() || title);
  if (Platform.OS === 'web') {
    if (typeof globalThis.Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(title, { body: bodyText, tag: opts.type });
      } catch {
        /* ignore */
      }
    }
    return;
  }
  try {
    // Напоминание, день рождения, провал отложенной отправки: своего звука приложение здесь
    // не играет, поэтому канал звучащий — но звуком темы, а не системным «default».
    const channelId = await ensureMessageChannel();
    await notifee.displayNotification({
      title,
      body: bodyText,
      data: dataToStringRecord({ type: opts.type, title, body: bodyText, ...(opts.extra ?? {}) }),
      android: {
        channelId,
        importance: AndroidImportance.HIGH,
        pressAction: { id: 'default', launchActivity: 'default' },
      },
    });
  } catch {
    /* ignore */
  }
}

export async function notifyContactRequestPush(opts: {
  title?: string;
  body?: string;
  requesterId?: string;
  requesterName?: string;
}): Promise<void> {
  await ensureNotifyPermission();
  const title =
    typeof opts.title === 'string' && opts.title.trim().length > 0
      ? opts.title.trim()
      : tr('notifyContactRequestTitle');
  const fromBody = typeof opts.body === 'string' && opts.body.trim().length > 0 ? opts.body.trim() : '';
  const fromName =
    typeof opts.requesterName === 'string' && opts.requesterName.trim().length > 0
      ? `${opts.requesterName.trim()} ${tr('notifyContactRequestBody')}`
      : '';
  const bodyText = truncateNotifyBody(fromBody || fromName || tr('notifyContactRequestBody'));
  if (Platform.OS === 'web') {
    if (typeof globalThis.Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        new Notification(title, { body: bodyText, tag: 'contact-request' });
      } catch {
        /* ignore */
      }
    }
    return;
  }
  try {
    // Заявка в друзья: своего звука у неё тоже нет, канал звучащий.
    const channelId = await ensureMessageChannel();
    await notifee.displayNotification({
      title,
      body: bodyText,
      data: dataToStringRecord({
        type: 'contact_request',
        title,
        body: bodyText,
        ...(opts.requesterId ? { requesterId: opts.requesterId } : {}),
        ...(opts.requesterName ? { requesterName: opts.requesterName } : {}),
      }),
      android: {
        channelId,
        importance: AndroidImportance.HIGH,
        pressAction: { id: 'default', launchActivity: 'default' },
      },
    });
  } catch {
    /* ignore */
  }
}
